import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";

interface Secretary {
  id: number;
  name: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  is_main_secretary: boolean;
  assigned_doctor_id: number | null;
  assigned_doctor: {
    id: number;
    name: string;
    specialty: string;
  } | null;
}
type DayOfWeek =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

interface ScheduleDay {
  day: DayOfWeek;
  start_time: string;
  end_time: string;
  is_available: boolean;
}
interface Doctor {
  id: number;
  name: string;
  first_name: string;
  last_name: string;
  specialty: string;
  email: string;
  phone: string;
  assigned_secretaries_count: number;
  salary?: number;
  is_active: boolean;
  created_at?: string;
}

type StaffType = "doctor" | "secretary";

interface StaffFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  password: string;
  specialty?: string;
  salary?: number;
  assigned_doctor_id?: number;
  is_main_secretary?: boolean;
}

const StaffManagement: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const [secretaries, setSecretaries] = useState<Secretary[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [availableDoctors, setAvailableDoctors] = useState<Doctor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingSecretary, setEditingSecretary] = useState<Secretary | null>(
    null
  );
  const [editingDoctor, setEditingDoctor] = useState<Doctor | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createStaffType, setCreateStaffType] =
    useState<StaffType>("secretary");
  const [activeTab, setActiveTab] = useState<"secretaries" | "doctors">(
    "secretaries"
  );
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [editingDoctorSchedule, setEditingDoctorSchedule] =
    useState<Doctor | null>(null);
  const [scheduleData, setScheduleData] = useState<any[]>([]);

  const API_BASE =
    process.env.REACT_APP_API_URL ||
    "https://dr-bassam-elayoubi.caduceuslb.com/api";

  useEffect(() => {
    if (isAdmin()) {
      loadData();
    }
  }, [isAdmin]);
  const loadDoctorSchedule = async (doctorId: number) => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_BASE}/doctor-schedules/${doctorId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setScheduleData(data.data || []);
      }
    } catch (err) {
      console.error("Error loading schedule:", err);
    }
  };

  const handleSaveSchedule = async (doctorId: number, scheduleData: any[]) => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_BASE}/doctor-schedules/${doctorId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ schedule: scheduleData }),
      });

      if (response.ok) {
        setShowScheduleForm(false);
        setEditingDoctorSchedule(null);
        showSuccessNotification("Doctor schedule updated successfully");
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Failed to update schedule");
      }
    } catch (err) {
      setError("Failed to update doctor schedule");
      console.error("Error updating schedule:", err);
    }
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("auth_token");

      const [secretariesResponse, doctorsResponse, availableDoctorsResponse] =
        await Promise.all([
          fetch(`${API_BASE}/secretary-management/secretaries`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE}/secretary-management/doctors`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE}/secretary-management/available-doctors`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

      if (
        secretariesResponse.ok &&
        doctorsResponse.ok &&
        availableDoctorsResponse.ok
      ) {
        const secretariesData = await secretariesResponse.json();
        const doctorsData = await doctorsResponse.json();
        const availableDoctorsData = await availableDoctorsResponse.json();

        setSecretaries(secretariesData.data || []);
        setDoctors(doctorsData.data || []);
        setAvailableDoctors(availableDoctorsData.data || []);
      } else {
        setError("Failed to load data");
      }
    } catch (err) {
      setError("Failed to load staff management data");
      console.error("Error loading data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssignSecretary = async (
    secretaryId: number,
    doctorId: number | null,
    isMainSecretary: boolean
  ) => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${API_BASE}/secretary-management/assign-secretary-to-doctor`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            secretary_id: secretaryId,
            doctor_id: doctorId,
            is_main_secretary: isMainSecretary,
          }),
        }
      );

      if (response.ok) {
        await loadData();
        setEditingSecretary(null);
        showSuccessNotification("Secretary assignment updated successfully");
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Failed to update assignment");
      }
    } catch (err) {
      setError("Failed to update secretary assignment");
      console.error("Error updating assignment:", err);
    }
  };

  const handleUnassignSecretary = async (secretaryId: number) => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${API_BASE}/secretary-management/unassign-secretary/${secretaryId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        await loadData();
        showSuccessNotification("Secretary unassigned successfully");
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Failed to unassign secretary");
      }
    } catch (err) {
      setError("Failed to unassign secretary");
      console.error("Error unassigning secretary:", err);
    }
  };

  const handleCreateStaff = async (formData: StaffFormData) => {
    try {
      const token = localStorage.getItem("auth_token");
      const endpoint =
        createStaffType === "doctor"
          ? `${API_BASE}/secretary-management/create-doctor`
          : `${API_BASE}/secretary-management/create-secretary`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        await loadData();
        setShowCreateForm(false);
        showSuccessNotification(
          `${
            createStaffType === "doctor" ? "Doctor" : "Secretary"
          } created successfully`
        );
      } else {
        const errorData = await response.json();
        setError(errorData.message || `Failed to create ${createStaffType}`);
      }
    } catch (err) {
      setError(`Failed to create ${createStaffType}`);
      console.error("Error creating staff:", err);
    }
  };

  const handleUpdateDoctor = async (
    doctorId: number,
    formData: Partial<StaffFormData>
  ) => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${API_BASE}/secretary-management/doctors/${doctorId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      if (response.ok) {
        await loadData();
        setEditingDoctor(null);
        showSuccessNotification("Doctor updated successfully");
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Failed to update doctor");
      }
    } catch (err) {
      setError("Failed to update doctor");
      console.error("Error updating doctor:", err);
    }
  };

  const handleToggleDoctorStatus = async (doctorId: number) => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${API_BASE}/secretary-management/doctors/${doctorId}/toggle-status`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        await loadData();
        showSuccessNotification("Doctor status updated successfully");
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Failed to update doctor status");
      }
    } catch (err) {
      setError("Failed to update doctor status");
      console.error("Error updating doctor status:", err);
    }
  };

  const showSuccessNotification = (message: string) => {
    const notification = document.createElement("div");
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #10b981;
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      z-index: 1000;
      font-weight: 500;
      font-size: 14px;
    `;
    notification.textContent = `✅ ${message}`;
    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), 3000);
  };

  // Styles
  const containerStyle: React.CSSProperties = {
    padding: "24px",
    maxWidth: "1200px",
    margin: "0 auto",
  };

  const headerStyle: React.CSSProperties = {
    marginBottom: "32px",
  };

  const cardStyle: React.CSSProperties = {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "16px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
  };

  const buttonStyle = (
    variant: "primary" | "secondary" | "danger" = "secondary"
  ): React.CSSProperties => {
    const variants = {
      primary: { bg: "#3b82f6", text: "#ffffff" },
      secondary: { bg: "#f3f4f6", text: "#374151" },
      danger: { bg: "#ef4444", text: "#ffffff" },
    };

    const colors = variants[variant];

    return {
      padding: "8px 16px",
      fontSize: "14px",
      fontWeight: "500",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      background: colors.bg,
      color: colors.text,
      transition: "all 0.2s ease",
    };
  };

  const tabStyle = (isActive: boolean): React.CSSProperties => ({
    padding: "12px 24px",
    fontSize: "14px",
    fontWeight: "600",
    border: "none",
    borderBottom: isActive ? "2px solid #3b82f6" : "2px solid transparent",
    background: "transparent",
    color: isActive ? "#3b82f6" : "#6b7280",
    cursor: "pointer",
    transition: "all 0.2s ease",
  });

  if (!isAdmin()) {
    return (
      <div style={containerStyle}>
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "#ef4444",
          }}
        >
          <h3>Access Denied</h3>
          <p>Only administrators can manage staff.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #e5e7eb",
              borderTop: "4px solid #3b82f6",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 16px auto",
            }}
          ></div>
          <p>Loading staff management...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h1
          style={{
            fontSize: "28px",
            fontWeight: "600",
            color: "#1f2937",
            marginBottom: "8px",
          }}
        >
          Staff Management
        </h1>
        <p style={{ color: "#6b7280", fontSize: "16px" }}>
          Manage doctors, secretaries, and their assignments
        </p>
      </div>

      {error && (
        <div
          style={{
            padding: "12px 16px",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: "8px",
            color: "#dc2626",
            marginBottom: "24px",
          }}
        >
          {error}
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{ marginBottom: "24px", borderBottom: "1px solid #e5e7eb" }}>
        <div style={{ display: "flex", gap: "0" }}>
          <button
            onClick={() => setActiveTab("secretaries")}
            style={tabStyle(activeTab === "secretaries")}
          >
            Secretaries ({secretaries.length})
          </button>
          <button
            onClick={() => setActiveTab("doctors")}
            style={tabStyle(activeTab === "doctors")}
          >
            Doctors ({doctors.length})
          </button>
        </div>
      </div>

      {/* Secretaries Tab */}
      {activeTab === "secretaries" && (
        <div style={cardStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
            }}
          >
            <h2
              style={{
                fontSize: "20px",
                fontWeight: "600",
                color: "#1f2937",
                margin: 0,
              }}
            >
              Current Secretaries ({secretaries.length})
            </h2>
            <button
              onClick={() => {
                setCreateStaffType("secretary");
                setShowCreateForm(true);
              }}
              style={buttonStyle("primary")}
            >
              + Add Secretary
            </button>
          </div>

          {secretaries.length === 0 ? (
            <div
              style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}
            >
              <p>No secretaries found. Add a secretary to get started.</p>
            </div>
          ) : (
            <div style={{ display: "grid", gap: "16px" }}>
              {secretaries.map((secretary) => (
                <div
                  key={secretary.id}
                  style={{
                    padding: "16px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    background: "#f9fafb",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "start",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <h3
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          color: "#1f2937",
                          margin: "0 0 8px 0",
                        }}
                      >
                        {secretary.name}
                      </h3>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#6b7280",
                          marginBottom: "8px",
                        }}
                      >
                        📧 {secretary.email}{" "}
                        {secretary.phone && `• 📞 ${secretary.phone}`}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          flexWrap: "wrap",
                        }}
                      >
                        {secretary.is_main_secretary ? (
                          <span
                            style={{
                              padding: "4px 12px",
                              background: "#dbeafe",
                              color: "#1e40af",
                              borderRadius: "12px",
                              fontSize: "12px",
                              fontWeight: "500",
                            }}
                          >
                            👑 Main Secretary
                          </span>
                        ) : secretary.assigned_doctor ? (
                          <span
                            style={{
                              padding: "4px 12px",
                              background: "#d1fae5",
                              color: "#065f46",
                              borderRadius: "12px",
                              fontSize: "12px",
                              fontWeight: "500",
                            }}
                          >
                            👨‍⚕️ Assigned to Dr. {secretary.assigned_doctor.name}
                            {secretary.assigned_doctor.specialty &&
                              ` (${secretary.assigned_doctor.specialty})`}
                          </span>
                        ) : (
                          <span
                            style={{
                              padding: "4px 12px",
                              background: "#fef3c7",
                              color: "#92400e",
                              borderRadius: "12px",
                              fontSize: "12px",
                              fontWeight: "500",
                            }}
                          >
                            ⚠️ Unassigned
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={() => setEditingSecretary(secretary)}
                        style={buttonStyle("secondary")}
                      >
                        Edit Assignment
                      </button>
                      {(secretary.assigned_doctor_id ||
                        secretary.is_main_secretary) && (
                        <button
                          onClick={() => handleUnassignSecretary(secretary.id)}
                          style={buttonStyle("danger")}
                        >
                          Unassign
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Doctors Tab */}
      {activeTab === "doctors" && (
        <div style={cardStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
            }}
          >
            <h2
              style={{
                fontSize: "20px",
                fontWeight: "600",
                color: "#1f2937",
                margin: 0,
              }}
            >
              Current Doctors ({doctors.length})
            </h2>
            <button
              onClick={() => {
                setCreateStaffType("doctor");
                setShowCreateForm(true);
              }}
              style={buttonStyle("primary")}
            >
              + Add Doctor
            </button>
          </div>

          {doctors.length === 0 ? (
            <div
              style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}
            >
              <p>No doctors found. Add a doctor to get started.</p>
            </div>
          ) : (
            <div style={{ display: "grid", gap: "16px" }}>
              {doctors.map((doctor) => (
                <div
                  key={doctor.id}
                  style={{
                    padding: "16px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    background: doctor.is_active ? "#f9fafb" : "#fef2f2",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "start",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <h3
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          color: doctor.is_active ? "#1f2937" : "#ef4444",
                          margin: "0 0 8px 0",
                        }}
                      >
                        Dr. {doctor.name}
                        {!doctor.is_active && " (Inactive)"}
                      </h3>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#6b7280",
                          marginBottom: "8px",
                        }}
                      >
                        {doctor.specialty && `🏥 ${doctor.specialty} • `}
                        📧 {doctor.email}
                        {doctor.phone && ` • 📞 ${doctor.phone}`}
                        {doctor.salary && ` • 💰 $${doctor.salary}/month`}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            padding: "4px 12px",
                            background: doctor.is_active
                              ? "#d1fae5"
                              : "#fef2f2",
                            color: doctor.is_active ? "#065f46" : "#dc2626",
                            borderRadius: "12px",
                            fontSize: "12px",
                            fontWeight: "500",
                          }}
                        >
                          {doctor.is_active ? "✅ Active" : "❌ Inactive"}
                        </span>
                        <span
                          style={{
                            padding: "4px 12px",
                            background: "#f0f9ff",
                            color: "#1e40af",
                            borderRadius: "12px",
                            fontSize: "12px",
                            fontWeight: "500",
                          }}
                        >
                          👥 {doctor.assigned_secretaries_count} secretary(ies)
                        </span>
                      </div>
                    </div>

                    <div
                      style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}
                    >
                      <button
                        onClick={() => setEditingDoctor(doctor)}
                        style={buttonStyle("secondary")}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleToggleDoctorStatus(doctor.id)}
                        style={buttonStyle(
                          doctor.is_active ? "danger" : "primary"
                        )}
                      >
                        {doctor.is_active ? "Deactivate" : "Activate"}
                      </button>
                      <button
                        onClick={() => {
                          setEditingDoctorSchedule(doctor);
                          loadDoctorSchedule(doctor.id);
                          setShowScheduleForm(true);
                        }}
                        style={buttonStyle("secondary")}
                      >
                        Manage Schedule
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Staff Form Modal */}
      {showCreateForm && (
        <CreateStaffModal
          staffType={createStaffType}
          availableDoctors={availableDoctors}
          onSubmit={handleCreateStaff}
          onClose={() => setShowCreateForm(false)}
        />
      )}

      {/* Edit Secretary Assignment Modal */}
      {editingSecretary && (
        <EditSecretaryModal
          secretary={editingSecretary}
          availableDoctors={availableDoctors}
          onSubmit={handleAssignSecretary}
          onClose={() => setEditingSecretary(null)}
        />
      )}

      {/* Edit Doctor Modal */}
      {editingDoctor && (
        <EditDoctorModal
          doctor={editingDoctor}
          onSubmit={handleUpdateDoctor}
          onClose={() => setEditingDoctor(null)}
        />
      )}

      {/* Doctor Schedule Modal */}
      {showScheduleForm && editingDoctorSchedule && (
        <DoctorScheduleModal
          doctor={editingDoctorSchedule}
          scheduleData={scheduleData}
          onSubmit={handleSaveSchedule}
          onClose={() => {
            setShowScheduleForm(false);
            setEditingDoctorSchedule(null);
            setScheduleData([]);
          }}
        />
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

// Create Staff Modal Component
const CreateStaffModal: React.FC<{
  staffType: StaffType;
  availableDoctors: Doctor[];
  onSubmit: (data: StaffFormData) => void;
  onClose: () => void;
}> = ({ staffType, availableDoctors, onSubmit, onClose }) => {
  const [formData, setFormData] = useState<StaffFormData>({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    password: "",
    specialty: "",
    salary: undefined,
    assigned_doctor_id: undefined,
    is_main_secretary: false,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: "12px",
          padding: "24px",
          maxWidth: "500px",
          width: "90%",
          maxHeight: "80vh",
          overflow: "auto",
        }}
      >
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "600",
            marginBottom: "20px",
          }}
        >
          Add New {staffType === "doctor" ? "Doctor" : "Secretary"}
        </h3>

        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gap: "16px" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "12px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "4px",
                  }}
                  htmlFor="first_name"
                >
                  First Name <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  required
                  value={formData.first_name}
                  onChange={(e) =>
                    setFormData({ ...formData, first_name: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "4px",
                  }}
                  htmlFor="last_name"
                >
                  Last Name *
                </label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  required
                  value={formData.last_name}
                  onChange={(e) =>
                    setFormData({ ...formData, last_name: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                />
              </div>
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "4px",
                }}
                htmlFor="email"
              >
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "12px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "4px",
                  }}
                  htmlFor="phone"
                >
                  Phone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "4px",
                  }}
                  htmlFor="password"
                >
                  Password *
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  required
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                />
              </div>
            </div>

            {staffType === "doctor" && (
              <>
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "500",
                      marginBottom: "4px",
                    }}
                    htmlFor="specialty"
                  >
                    Specialty
                  </label>
                  <input
                    type="text"
                    id="specialty"
                    name="specialty"
                    value={formData.specialty}
                    onChange={(e) =>
                      setFormData({ ...formData, specialty: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "500",
                      marginBottom: "4px",
                    }}
                    htmlFor="salary"
                  >
                    Monthly Salary
                  </label>
                  <input
                    type="number"
                    id="salary"
                    name="salary"
                    min="0"
                    step="0.01"
                    value={formData.salary || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        salary: e.target.value
                          ? parseFloat(e.target.value)
                          : undefined,
                      })
                    }
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                    }}
                  />
                </div>
              </>
            )}

            {staffType === "secretary" && (
              <>
                <div>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "8px",
                    }}
                    htmlFor="is_main_secretary"
                  >
                    <input
                      type="checkbox"
                      id="is_main_secretary"
                      name="is_main_secretary"
                      checked={formData.is_main_secretary}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          is_main_secretary: e.target.checked,
                          assigned_doctor_id: e.target.checked
                            ? undefined
                            : formData.assigned_doctor_id,
                        })
                      }
                    />
                    <span>Main Secretary (can see all appointments)</span>
                  </label>
                </div>

                {!formData.is_main_secretary && (
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "500",
                        marginBottom: "4px",
                      }}
                      htmlFor="assigned_doctor_id"
                    >
                      Assign to Doctor
                    </label>
                    <select
                      id="assigned_doctor_id"
                      name="assigned_doctor_id"
                      value={formData.assigned_doctor_id || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          assigned_doctor_id: e.target.value
                            ? parseInt(e.target.value)
                            : undefined,
                        })
                      }
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: "14px",
                      }}
                    >
                      <option value="">Select a doctor...</option>
                      {availableDoctors.map((doctor) => (
                        <option key={doctor.id} value={doctor.id}>
                          Dr. {doctor.name}{" "}
                          {doctor.specialty && `(${doctor.specialty})`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            )}
          </div>

          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
              marginTop: "20px",
            }}
          >
            <button
              type="button"
              id="cancel_button"
              name="cancel_button"
              onClick={onClose}
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                background: "#f3f4f6",
                color: "#374151",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              id="submit_button"
              name="submit_button"
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                background: "#3b82f6",
                color: "#ffffff",
              }}
            >
              Create {staffType === "doctor" ? "Doctor" : "Secretary"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Secretary Modal Component
const EditSecretaryModal: React.FC<{
  secretary: Secretary;
  availableDoctors: Doctor[];
  onSubmit: (
    secretaryId: number,
    doctorId: number | null,
    isMainSecretary: boolean
  ) => void;
  onClose: () => void;
}> = ({ secretary, availableDoctors, onSubmit, onClose }) => {
  const [assignmentType, setAssignmentType] = useState<"main" | "doctor">(
    secretary.is_main_secretary ? "main" : "doctor"
  );
  const [selectedDoctorId, setSelectedDoctorId] = useState<number | undefined>(
    secretary.assigned_doctor_id || undefined
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const isMainSecretary = assignmentType === "main";
    const doctorId = isMainSecretary ? null : selectedDoctorId || null;
    onSubmit(secretary.id, doctorId, isMainSecretary);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: "12px",
          padding: "24px",
          maxWidth: "500px",
          width: "90%",
          maxHeight: "80vh",
          overflow: "auto",
        }}
      >
        <h3
          style={{ fontSize: "18px", fontWeight: "600", marginBottom: "20px" }}
        >
          Edit Assignment: {secretary.name}
        </h3>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                marginBottom: "8px",
              }}
            >
              Assignment Type
            </label>

            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "8px",
                }}
              >
                <input
                  type="radio"
                  id="assignment_type_main"
                  name="assignment_type"
                  value="main"
                  checked={assignmentType === "main"}
                  onChange={(e) =>
                    setAssignmentType(e.target.value as "main" | "doctor")
                  }
                />

                <span>Main Secretary (can see all appointments)</span>
              </label>

              <label
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <input
                  type="radio"
                  id="assignment_type_doctor"
                  name="assignment_type"
                  value="doctor"
                  checked={assignmentType === "doctor"}
                  onChange={(e) =>
                    setAssignmentType(e.target.value as "main" | "doctor")
                  }
                />

                <span>Doctor's Secretary (assigned to specific doctor)</span>
              </label>
            </div>

            {assignmentType === "doctor" && (
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  Assign to Doctor
                </label>
                <select
                  value={selectedDoctorId || ""}
                  onChange={(e) =>
                    setSelectedDoctorId(
                      e.target.value ? parseInt(e.target.value) : undefined
                    )
                  }
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                >
                  <option value="">Select a doctor...</option>
                  {availableDoctors.map((doctor) => (
                    <option key={doctor.id} value={doctor.id}>
                      Dr. {doctor.name}{" "}
                      {doctor.specialty && `(${doctor.specialty})`}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div
            style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                background: "#f3f4f6",
                color: "#374151",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                background: "#3b82f6",
                color: "#ffffff",
              }}
            >
              Save Assignment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
// Doctor Schedule Modal Component
const DoctorScheduleModal: React.FC<{
  doctor: Doctor;
  scheduleData: any[];
  onSubmit: (doctorId: number, scheduleData: ScheduleDay[]) => void;
  onClose: () => void;
}> = ({ doctor, scheduleData, onSubmit, onClose }) => {
  const [schedule, setSchedule] = useState<ScheduleDay[]>(() => {
    const defaultSchedule: ScheduleDay[] = [
      {
        day: "monday",
        start_time: "09:00",
        end_time: "17:00",
        is_available: true,
      },
      {
        day: "tuesday",
        start_time: "09:00",
        end_time: "17:00",
        is_available: true,
      },
      {
        day: "wednesday",
        start_time: "09:00",
        end_time: "17:00",
        is_available: true,
      },
      {
        day: "thursday",
        start_time: "09:00",
        end_time: "17:00",
        is_available: true,
      },
      {
        day: "friday",
        start_time: "09:00",
        end_time: "17:00",
        is_available: true,
      },
      {
        day: "saturday",
        start_time: "09:00",
        end_time: "17:00",
        is_available: false,
      },
      {
        day: "sunday",
        start_time: "09:00",
        end_time: "17:00",
        is_available: false,
      },
    ];

    if (scheduleData.length > 0) {
      return defaultSchedule.map((defaultDay) => {
        const existingDay = scheduleData.find(
          (s) => s.day_of_week === defaultDay.day
        );
        return existingDay
          ? {
              day: existingDay.day_of_week as DayOfWeek,
              start_time: existingDay.start_time,
              end_time: existingDay.end_time,
              is_available: existingDay.is_available,
            }
          : defaultDay;
      });
    }

    return defaultSchedule;
  });

  const dayNames: Record<DayOfWeek, string> = {
    monday: "Monday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
    thursday: "Thursday",
    friday: "Friday",
    saturday: "Saturday",
    sunday: "Sunday",
  };

  const updateScheduleDay = (dayIndex: number, field: string, value: any) => {
    const newSchedule = [...schedule];
    newSchedule[dayIndex] = { ...newSchedule[dayIndex], [field]: value };
    setSchedule(newSchedule);
  };

  const applyToAllDays = (sourceIndex: number) => {
    const sourceDay = schedule[sourceIndex];
    const newSchedule = schedule.map((day) => ({
      ...day,
      start_time: sourceDay.start_time,
      end_time: sourceDay.end_time,
      is_available: sourceDay.is_available,
    }));
    setSchedule(newSchedule);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(doctor.id, schedule);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: "12px",
          padding: "24px",
          maxWidth: "600px",
          width: "90%",
          maxHeight: "80vh",
          overflow: "auto",
        }}
      >
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "600",
            marginBottom: "20px",
          }}
        >
          Manage Schedule: Dr. {doctor.name}
        </h3>

        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gap: "16px" }}>
            {schedule.map((day, index) => (
              <div
                key={day.day}
                style={{
                  padding: "16px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  background: "#f9fafb",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "12px",
                  }}
                >
                  <h4
                    style={{
                      fontSize: "16px",
                      fontWeight: "500",
                      margin: 0,
                    }}
                  >
                    {dayNames[day.day]}
                  </h4>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <label
                      htmlFor={`day_available_${index}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={day.is_available}
                        onChange={(e) =>
                          updateScheduleDay(
                            index,
                            "is_available",
                            e.target.checked
                          )
                        }
                      />
                      Available
                    </label>
                    <button
                      type="button"
                      onClick={() => applyToAllDays(index)}
                      style={{
                        padding: "4px 8px",
                        fontSize: "12px",
                        background: "#e5e7eb",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      Apply to All
                    </button>
                  </div>
                </div>

                {day.is_available && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "12px",
                    }}
                  >
                    <div>
                      <label
                        htmlFor={`start_time_${index}`}
                        style={{
                          display: "block",
                          fontSize: "14px",
                          fontWeight: "500",
                          marginBottom: "4px",
                        }}
                      >
                        Start Time
                      </label>

                      <input
                        type="time"
                        id={`start_time_${index}`}
                        name={`start_time_${index}`}
                        value={day.start_time}
                        onChange={(e) =>
                          updateScheduleDay(index, "start_time", e.target.value)
                        }
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          fontSize: "14px",
                        }}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor={`end_time_${index}`}
                        style={{
                          display: "block",
                          fontSize: "14px",
                          fontWeight: "500",
                          marginBottom: "4px",
                        }}
                      >
                        End Time
                      </label>

                      <input
                        type="time"
                        id={`end_time_${index}`}
                        name={`end_time_${index}`}
                        value={day.end_time}
                        onChange={(e) =>
                          updateScheduleDay(index, "end_time", e.target.value)
                        }
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          fontSize: "14px",
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
              marginTop: "20px",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                background: "#f3f4f6",
                color: "#374151",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                background: "#3b82f6",
                color: "#ffffff",
              }}
            >
              Save Schedule
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
// Edit Doctor Modal Component
const EditDoctorModal: React.FC<{
  doctor: Doctor;
  onSubmit: (doctorId: number, data: Partial<StaffFormData>) => void;
  onClose: () => void;
}> = ({ doctor, onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    first_name: doctor.first_name,
    last_name: doctor.last_name,
    email: doctor.email,
    phone: doctor.phone || "",
    specialty: doctor.specialty || "",
    salary: doctor.salary || undefined,
    password: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const updateData: Partial<StaffFormData> = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      phone: formData.phone,
      specialty: formData.specialty,
      salary: formData.salary,
    };

    // Only include password if it's been entered
    if (formData.password.trim()) {
      updateData.password = formData.password;
    }

    onSubmit(doctor.id, updateData);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: "12px",
          padding: "24px",
          maxWidth: "500px",
          width: "90%",
          maxHeight: "80vh",
          overflow: "auto",
        }}
      >
        <h3
          style={{ fontSize: "18px", fontWeight: "600", marginBottom: "20px" }}
        >
          Edit Doctor: {doctor.name}
        </h3>

        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gap: "16px" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "12px",
              }}
            >
              <div>
                <label
                  htmlFor="first_name"
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "4px",
                  }}
                >
                  First Name <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  required
                  value={formData.first_name}
                  onChange={(e) =>
                    setFormData({ ...formData, first_name: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                />
              </div>
              <div>
                <label
                  htmlFor="last_name"
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "4px",
                  }}
                >
                  Last Name <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  required
                  value={formData.last_name}
                  onChange={(e) =>
                    setFormData({ ...formData, last_name: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="email"
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "4px",
                }}
              >
                Email <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "12px",
              }}
            >
              <div>
                <label
                  htmlFor="phone"
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "4px",
                  }}
                >
                  Phone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                />
              </div>
              <div>
                <label
                  htmlFor="specialty"
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "4px",
                  }}
                >
                  Specialty
                </label>
                <input
                  type="text"
                  id="specialty"
                  name="specialty"
                  value={formData.specialty}
                  onChange={(e) =>
                    setFormData({ ...formData, specialty: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                />
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "12px",
              }}
            >
              <div>
                <label
                  htmlFor="salary"
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "4px",
                  }}
                >
                  Monthly Salary
                </label>
                <input
                  type="number"
                  id="salary"
                  name="salary"
                  min="0"
                  step="0.01"
                  value={formData.salary || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      salary: e.target.value
                        ? parseFloat(e.target.value)
                        : undefined,
                    })
                  }
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                />
              </div>
              <div>
                <label
                  htmlFor="password"
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "4px",
                  }}
                >
                  New Password (optional)
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  placeholder="Leave blank to keep current"
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                />
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
              marginTop: "20px",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                background: "#f3f4f6",
                color: "#374151",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "500",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                background: "#3b82f6",
                color: "#ffffff",
              }}
            >
              Update Doctor
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StaffManagement;
